import {PropertyTypeConstructor} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ErrorOrEmpty, IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABinaryData} from '@mintdata/mint-kit/api/values/ABinaryData';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AByte} from '@mintdata/mint-kit/api/values/AByte';
import {ACurrency} from '@mintdata/mint-kit/api/values/ACurrency';
import {ADate} from '@mintdata/mint-kit/api/values/ADate';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AIntegerEnum} from '@mintdata/mint-kit/api/values/AIntegerEnum';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AStringEnum} from '@mintdata/mint-kit/api/values/AStringEnum';
import {ATime} from '@mintdata/mint-kit/api/values/ATime';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame, IFrameRecord} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {BaseCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/BaseCoercionUtil';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';

export class FrameCoercionUtil extends BaseCoercionUtil
{
    constructor()
    {
        super(Type.Boolean);
    }

    public toBoolean(from:IFrame):ABoolean | ErrorOrEmpty
    {
        if( !from )
            return AError.fromString('#FrameIsAbsent');

        if( from.isEmpty )
            return AError.fromString('#FrameIsEmpty');

        return new ABoolean(from.getRowsCount() > 0);
    }

    public toByte(from:IFrame):AByte | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.Byte) as any;
    }

    toBinaryData(from:IFrame):ABinaryData | ErrorOrEmpty
    {
        const aString = new AString(JsonUtil.convertFrameToJson(from));
        return CoercionUtils.coerce(aString, Type.BinaryData, null) as ABinaryData;
    }

    public toCurrency(from:IFrame):ACurrency | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.Currency) as any;
    }

    public toDateTime(from:IFrame):ADateTime | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.DateTime) as any;
    }

    public toDate(from:IFrame):ADate | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.Date) as any;
    }

    public toTime(from:IFrame):ATime | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.Time) as any;
    }

    public toIntegerEnum(from:IFrame):AIntegerEnum | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.IntegerEnum) as any;
    }

    public toStringEnum(from:IFrame, toCtor?:{new():IValue}):AStringEnum | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.StringEnum, toCtor) as any;
    }

    public toInteger(from:IFrame):AInteger | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.Integer) as any;
    }

    public toNumber(from:IFrame):ANumber | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, Type.Number) as any;
    }

    public toString(from:IFrame):AString | ErrorOrEmpty
    {
        if( !from )
            return AError.fromString('#FrameIsAbsent');

        if( from.isEmpty )
            return AError.fromString('#FrameIsEmpty');

        const recordsStrings:Array<string> = [];

        const records:Array<IFrameRecord> = from.getRecords();

        records.forEach((record, index) =>
        {
            recordsStrings.push(
                record.getCells().map((value:IValue) =>
                {
                    return CoercionUtils.coerce(value, Type.String, null);
                }).join(', ')
            );
        });

        return AString.fromString(recordsStrings.join('\n'));
    }

    public toFrame(from:IFrame):AFrame | ErrorOrEmpty
    {
        return from as any;
    }

    public toCustom(from:IFrame, toCtor:PropertyTypeConstructor<any>):any | ErrorOrEmpty
    {
        return this._coerceSingleCell(from, null, toCtor) as any;
    }

    private _coerceSingleCell(from:IFrame, toType:Type, toCtor?:PropertyTypeConstructor<any>):IValue
    {
        if( !from )
            return AError.fromString('#FrameIsAbsent');

        if( from.isEmpty )
            // return null;
            return AError.fromString('#FrameIsEmpty');

        const fields:Array<FrameField> = from.getFields();
        const records:Array<IFrameRecord> = from.getRecords();

        if( from && (fields.length > 1 || records.length > 1) )
            return AError.fromString('#InvalidFrameShape!');

        return CoercionUtils.coerce(records[0].getCell(fields[0].name), toType, toCtor);
    }
}
